// An elapsed timer component that displays the time elapsed since the timer was started.
// It also has a callback function that is called every second with the elapsed time.
// It also has a callback function that is called once the timer is stopped.
// Uses dayJS for time manipulation and its relative time plugin.
// It should give output as the format HH:mm:ss.

import type React from 'react'
import { useEffect, useState } from 'react'
import dayjs from 'v2/utils/dayjs'

export interface ElapsedTimerProps {
  /**
   * The callback function that is called every second with the elapsed time.
   */
  onTick?: (elapsedTime: string) => void
  /**
   * The callback function that is called once the timer is stopped.
   */
  onStop?: () => void
  startTime: string
  countdownDuration?: number // New prop for countdown duration in seconds
  format?: string
  className?: string
}

const formatElapsedTime = (elapsedTime: number, format: string) => {
  const duration = dayjs.duration(elapsedTime, 'seconds')
  const days = Math.floor(duration.asDays())

  if (days > 0) {
    const hours = duration.hours()
    const minutes = duration.minutes()
    const seconds = duration.seconds()

    return `${days}d ${hours.toString().padStart(2, '0')}h ${minutes
      .toString()
      .padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`
  }
  return duration.format(format).replace(/^00:(\d{2}:\d{2})$/, '$1') // Remove leading 00: from the formatted time
}

export const ElapsedTimer: React.FC<ElapsedTimerProps> = ({
  className,
  onTick,
  onStop,
  startTime,
  countdownDuration = 0,
  format = 'HH:mm:ss',
}) => {
  const [elapsedTime, setElapsedTime] = useState(
    countdownDuration || dayjs().diff(startTime, 'second'),
  )
  const [isRunning, setIsRunning] = useState(true) // Define state and setter

  useEffect(() => {
    if (!isRunning) return // Guard clause for non-running state

    const interval = setInterval(() => {
      setElapsedTime((prevElapsedTime) => {
        const newElapsedTime = countdownDuration
          ? Math.max(prevElapsedTime - 1, 0)
          : dayjs().diff(startTime, 'second')

        if (onTick) onTick(formatElapsedTime(newElapsedTime, format))

        if (newElapsedTime <= 0 && countdownDuration) {
          clearInterval(interval)
          setIsRunning(false) // Stop the timer when it reaches zero
          if (onStop) onStop()
        }
        return newElapsedTime
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return <span className={className}>{formatElapsedTime(elapsedTime, format)}</span>
}
